import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import LucideIcon from "../../../components/LucideIcon";
import axios from "axios";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";
import { MARKETING_TEMPLATE_CONTENT_TOKEN } from "../../../constants";
import { Campaign } from "./CampaignFormDialog";
import { CampaignTemplate } from "./TemplateBuilderDialog";

interface ApplyTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (initialCampaign: Partial<Campaign>) => void;
  onError: (err: string) => void;
  companyId: string;
  getToken: () => Promise<string>;
  presetTemplateId?: string | null;
}

const ApplyTemplateDialog: React.FC<ApplyTemplateDialogProps> = ({
  open,
  onClose,
  onApply,
  onError,
  companyId,
  getToken,
  presetTemplateId,
}) => {
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setSelectedTemplateId(presetTemplateId || "");
    (async () => {
      setLoadingTemplates(true);
      try {
        const token = await getToken();
        const res = await axios.get(`${hostname}/marketing-campaign-templates`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        setTemplates(res.data?.items || []);
      } catch (err) {
        onError(parseError(err));
      } finally {
        setLoadingTemplates(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, presetTemplateId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const mergedHtml = useMemo(() => {
    if (!selectedTemplate) return "";
    const body = selectedTemplate.bodyHtml || "";
    return body.split(MARKETING_TEMPLATE_CONTENT_TOKEN).join("");
  }, [selectedTemplate]);

  const handleContinue = useCallback(() => {
    if (!selectedTemplate) {
      onError("Pick a template first.");
      return;
    }
    const initial: Partial<Campaign> = {
      name: `${selectedTemplate.name} — ${new Date().toLocaleDateString()}`,
      subject: selectedTemplate.subject || "",
      replyTo: (selectedTemplate as any).replyTo || null,
      bodyHtml: mergedHtml,
      bodyDocument: selectedTemplate.bodyDocument
        ? JSON.parse(JSON.stringify(selectedTemplate.bodyDocument))
        : null,
      senderProfileId: selectedTemplate.senderProfileId || null,
      listId: selectedTemplate.listId || null,
      fieldMappings: selectedTemplate.fieldMappings || {},
      scheduleSettings: (selectedTemplate as any).scheduleSettings || null,
    };
    onApply(initial);
  }, [selectedTemplate, mergedHtml, onApply, onError]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        New Campaign from Template
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <LucideIcon name="X" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControl fullWidth required>
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value as string)}
              disabled={loadingTemplates}
            >
              {templates.length === 0 && !loadingTemplates && (
                <MenuItem value="" disabled>
                  <em>No templates yet</em>
                </MenuItem>
              )}
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTemplate && (
            <Alert severity="info">
              Applying this template will pre-fill the campaign's subject, sender
              profile, smart list, and field mappings. You can adjust anything
              before sending.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            <Paper
              variant="outlined"
              sx={{ height: 600, overflow: "hidden", bgcolor: "background.paper" }}
            >
              <iframe
                title="Template preview"
                srcDoc={
                  selectedTemplate
                    ? mergedHtml
                    : "<em>Pick a template to see the preview.</em>"
                }
                style={{ width: "100%", height: "100%", border: "none" }}
                sandbox=""
              />
            </Paper>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedTemplate}
        >
          Continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ApplyTemplateDialog;
